import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Button,
  Radio,
  Tooltip,
  DatePicker,
  TimePicker,
  App,
  Alert,
} from "antd";
import {
  UploadOutlined,
  InfoCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import theme from "../themes";
import styled from "styled-components";
import Papa from "papaparse";
import * as yup from "yup";
import { ReactComponent as CheckCircle } from "../assets/icons/checkCircle.svg";
import {
  createCampaignThunk,
  getCampaignsThunk,
  getListAudiencesThunk,
  uploadFileThunk,
} from "../store/actions/campaign.action";
import { useAppDispatch, useAppSelector } from "../store";
import { getAgentsThunk } from "../store/actions/agent.action";
import ToastMessage from "../utils/toastMessage";
import { isValidAudiencePhone } from "../utils/phoneValidation";
import {
  convertScheduleToUTC,
  getTimezoneOptions,
  TIMEZONE_CONFIG,
  type ScheduleData,
} from "../utils/timezoneUtils";
import { getLinesThunk } from "../store/actions/line.action";
import {
  getEnabledPhoneChannels,
  inboundAgentIdForChannel,
  ILineResponse,
  LineStatus,
  PhoneChannelKey,
} from "../models/line.model";
import { IAgent, resolveAgentChannels, resolveAgentDirection } from "../models/agent.model";
import { useChannelSettings } from "../hooks/useChannelSettings";
import { CHANNEL_KEYS, ChannelKey } from "../models/company.model";
import dayjs from "dayjs";
import {
  formLabelCol,
  formOffsetCol,
  formWrapperCol,
} from "../styles/responsive";
import { isCampaignGreetingChannel } from "../pages/outbound/type";
import {
  extractTemplateVariableNames,
  isPhoneNumberHeader,
  normalizeVariableKey,
  stripHeader,
} from "../utils/templateVariables";
import CampaignMessagePreview from "./CampaignMessagePreview";

const { Option } = Select;

interface CreateCampaignModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const toast = ToastMessage(message);
  const [form] = Form.useForm();

  // Yup validation schema with translations
  const campaignSchema = yup.object().shape({
    campaignName: yup
      .string()
      .required(t("campaignModal.create.validation.campaignNameRequired")),
    phoneNumber: yup
      .string()
      .required(t("campaignModal.create.validation.phoneNumberRequired")),
    agent: yup.string().when("channel", {
      is: (channel: string) => !isCampaignGreetingChannel(channel),
      then: (schema) =>
        schema.required(t("campaignModal.create.validation.agentRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
    channel: yup
      .string()
      .oneOf(["call", "sms", "whatsapp", "email"])
      .required(t("campaignModal.create.validation.channelRequired")),
    greeting_message: yup.string().when("channel", {
      is: (channel: string) => isCampaignGreetingChannel(channel),
      then: (schema) =>
        schema
          .trim()
          .required(t("campaignModal.create.validation.messageRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
    recipientListId: yup
      .string()
      .required(t("campaignModal.create.validation.recipientRequired")),
    uploadedFile: yup.array().when("recipientListId", {
      is: (val: string) => val === "new",
      then: (schema) =>
        schema.min(1, t("campaignModal.create.validation.uploadedFile")),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const [file, setFile] = useState<File | null>(null);
  const [validCount, setValidCount] = useState<number | null>(null);
  const [invalidCount, setInvalidCount] = useState<number | null>(null);
  const [fileValidationError, setFileValidationError] = useState<string | null>(
    null
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isMissingPhoneColumn, setIsMissingPhoneColumn] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [timing, setTiming] = useState("immediate");
  const [schedule, setSchedule] = useState<ScheduleData>({
    date: null,
    time: null,
    ampm: "AM",
    tz: "EST",
  });
  const [isCreating, setIsCreating] = useState(false);
  const [wizardStep, setWizardStep] = useState<0 | 1>(0);
  const lastPrefillRef = useRef("");
  const dispatch = useAppDispatch();
  const [agents, setAgents] = useState<IAgent[]>([]);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const { lines } = useAppSelector((state) => state.line);
  const [audiences, setAudiences] = useState<any[]>([]);
  const [recipientSelection, setRecipientSelection] = useState<string | null>(
    null
  );

  const selectedAgentId = Form.useWatch("agent", form) as string | undefined;
  const selectedPhoneNumber = Form.useWatch("phoneNumber", form) as
    | string
    | undefined;
  const { isEnabled } = useChannelSettings();

  const selectedLine = useMemo(() => {
    return lines.lines.find(
      (line: ILineResponse) => line.line_number === selectedPhoneNumber
    );
  }, [lines.lines, selectedPhoneNumber]);

  const availableChannels = useMemo(() => {
    return CHANNEL_KEYS.filter((key) => {
      if (!isEnabled(key)) return false;
      if (key === "email") return true;
      if (key !== "call" && key !== "sms" && key !== "whatsapp") return false;
      if (selectedLine) {
        return getEnabledPhoneChannels(selectedLine).includes(key);
      }
      return (lines.lines || []).some((line: ILineResponse) =>
        getEnabledPhoneChannels(line).includes(key as PhoneChannelKey)
      );
    });
  }, [isEnabled, selectedLine, lines.lines]);

  const defaultChannel: ChannelKey = availableChannels[0] || "call";
  const selectedChannel =
    (Form.useWatch("channel", form) as string) || defaultChannel;
  const isMessagingCampaign =
    selectedChannel === "sms" || selectedChannel === "whatsapp";
  const showCampaignMessage = isCampaignGreetingChannel(selectedChannel);
  const showPreviewStep = showCampaignMessage;
  const onDetailsStep = !showPreviewStep || wizardStep === 0;

  const campaignPhoneLines = useMemo(() => {
    const all = lines.lines || [];
    if (!isMessagingCampaign) return all;
    return all.filter((line: ILineResponse) =>
      getEnabledPhoneChannels(line).includes(selectedChannel as PhoneChannelKey)
    );
  }, [lines.lines, selectedChannel, isMessagingCampaign]);

  const noOutboundMessagingNumbers =
    isMessagingCampaign && campaignPhoneLines.length === 0;

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent._id === selectedAgentId),
    [agents, selectedAgentId]
  );

  const hasEmailInboundAgent = useMemo(
    () =>
      agents.some((agent) =>
        resolveAgentChannels(agent.channels).includes("email")
      ),
    [agents]
  );

  const selectedLineMissingInboundAgent =
    showCampaignMessage &&
    (selectedChannel === "email"
      ? agentsLoaded && !hasEmailInboundAgent
      : isMessagingCampaign &&
        Boolean(selectedLine) &&
        !inboundAgentIdForChannel(selectedLine, selectedChannel));

  const agentGreeting = useMemo(
    () =>
      String(
        selectedAgent?.voice_configs?.greeting_message ||
          selectedAgent?.greeting_message ||
          ""
      ).trim(),
    [selectedAgent]
  );

  const campaignAgents = useMemo(
    () =>
      agents.filter((agent) => {
        const channels = resolveAgentChannels(agent.channels);
        if (
          !channels.includes(selectedChannel as (typeof CHANNEL_KEYS)[number])
        ) {
          return false;
        }
        if (selectedChannel === "call") {
          return resolveAgentDirection(channels, agent.direction) === "outbound";
        }
        return true;
      }),
    [agents, selectedChannel]
  );

  const greetingMessageValue = Form.useWatch("greeting_message", form) as
    | string
    | undefined;

  const agentTemplateVariables = useMemo(() => {
    const headers: string[] = [];
    const seen = new Set<string>();

    const pushHeader = (rawValue: string) => {
      const sanitized = stripHeader(rawValue)
        .replace(/^\{+/, "")
        .replace(/\}+$/, "")
        .trim();

      if (!sanitized) return;

      const normalized = normalizeVariableKey(sanitized);
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
      headers.push(normalized === "phone_number" ? "phone_number" : sanitized);
    };

    pushHeader("phone_number");
    extractTemplateVariableNames(greetingMessageValue).forEach(pushHeader);

    const addVariables = (variables?: Record<string, string>) => {
      if (!variables) return;
      Object.values(variables).forEach((variable) => {
        if (typeof variable !== "string") return;
        pushHeader(variable);
      });
    };

    addVariables(selectedAgent?.parameters?.system_variables);
    addVariables(selectedAgent?.parameters?.custom_variables);

    return headers;
  }, [selectedAgent, greetingMessageValue]);

  const templateCsvHref = useMemo(() => {
    if (agentTemplateVariables.length === 0) return undefined;
    const headerRow = agentTemplateVariables.join(",");
    return `data:text/csv;charset=utf-8,${encodeURIComponent(
      `${headerRow}\n`
    )}`;
  }, [agentTemplateVariables]);

  const downloadTemplateFilename = useMemo(() => {
    const sourceName = showCampaignMessage
      ? String(form.getFieldValue("campaignName") || "").trim()
      : selectedAgent?.agent_name?.trim() || "";
    if (!sourceName) return "campaign_template.csv";
    const sanitizedName = sourceName.toLowerCase().replace(/\s+/g, "_");
    return `${sanitizedName}_recipients.csv`;
  }, [form, selectedAgent, showCampaignMessage, greetingMessageValue]);

  const fetchAudiences = async () => {
    try {
      const res = await dispatch(getListAudiencesThunk()).unwrap();
      const filteredAudiences = (res.data || []).filter((aud: any) => {
        const name = (aud.audience_list_name || "").toLowerCase().trim();
        const containsTestUserFor = name.includes("test user for");
        const endsWithCsv = name.endsWith(".csv");
        return !(containsTestUserFor && !endsWithCsv);
      });
      setAudiences(filteredAudiences);
    } catch (error) {
      console.log("error", error);
    }
  };

  const fetchAgents = async () => {
    try {
      const res = await dispatch(
        getAgentsThunk({
          limit: 999,
          page: 1,
          keyword: "",
        })
      ).unwrap();
      setAgents(res?.data?.agents ?? []);
    } catch (error) {
      console.log("error", error);
    } finally {
      setAgentsLoaded(true);
    }
  };

  const fetchLines = async () => {
    try {
      await dispatch(
        getLinesThunk({
          limit: 999,
          page: 1,
          line_number: "",
          status: [LineStatus.ACTIVE, LineStatus.MESSAGE_SERVICE_PENDING],
          sort: {},
        })
      ).unwrap();
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleFileChange = ({ fileList }: any) => {
    if (fileList && fileList.length > 0) {
      const nativeFile = fileList[0].originFileObj;
      setFile(nativeFile);
      setFileValidationError(null);
      setInvalidCount(null);
      setIsMissingPhoneColumn(false);
      setTotalCount(null);

      if (nativeFile) {
        Papa.parse(nativeFile, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header: string) => stripHeader(header) || header,
          complete: (results: any) => {
            const data = results.data;
            if (data.length > 0) {
              const fields: string[] =
                (results.meta && results.meta.fields) || [];
              if (!fields.some((field) => isPhoneNumberHeader(field))) {
                setFileValidationError(
                  t("campaignModal.create.phoneNumberColumnMissing")
                );
                setIsMissingPhoneColumn(true);
                setValidCount(null);
                setInvalidCount(null);
                setTotalCount(null);
                return;
              }

              // Count valid and invalid phone numbers
              const validPhoneNumbers = new Set();
              let invalidCount = 0;
              let missingValueCount = 0;
              data.forEach((row: any) => {
                const phoneField = Object.keys(row || {}).find((key) =>
                  isPhoneNumberHeader(key)
                );
                const phoneNumber = phoneField ? row[phoneField] : row.phone_number;
                if (phoneNumber && phoneNumber.trim() !== "") {
                  const trimmedPhone = phoneNumber.trim();
                  if (isValidAudiencePhone(trimmedPhone)) {
                    validPhoneNumbers.add(trimmedPhone);
                  } else {
                    invalidCount++;
                  }
                } else {
                  missingValueCount++;
                }
              });

              const totalInvalid = invalidCount + missingValueCount;
              const totalRows = data.length;

              setValidCount(validPhoneNumbers.size);
              setInvalidCount(totalInvalid);
              setTotalCount(totalRows);

              // Treat any missing or invalid values as errors
              if (totalInvalid > 0) {
                setFileValidationError(
                  `${totalInvalid}/${totalRows} ${t(
                    "campaignModal.create.invalidPhoneNumbers"
                  )}`
                );
              }
            } else {
              setValidCount(0);
              setInvalidCount(0);
              setTotalCount(0);
            }
          },
          error: () => {
            setValidCount(null);
            setInvalidCount(null);
            setIsMissingPhoneColumn(false);
            setTotalCount(null);
            setFileValidationError(t("common.error"));
          },
        });
      }
    } else {
      setFile(null);
      setValidCount(null);
      setInvalidCount(null);
      setFileValidationError(null);
      setIsMissingPhoneColumn(false);
      setTotalCount(null);
    }
  };

  const performCreate = async () => {
    setIsCreating(true);
    try {
      // Get form values
      const formValues = await form.validateFields();

      // Prepare data for Yup validation
      const validationData = {
        ...formValues,
        uploadedFile: file ? [file] : [],
      };

      // Validate with Yup
      await campaignSchema.validate(validationData, { abortEarly: false });

      // Validate schedule fields when timing is "schedule"
      if (timing === "schedule" && (!schedule.date || !schedule.time)) {
        toast.error(
          t("campaignModal.create.validation.scheduleDateAndTimeRequired"),
          5
        );
        return;
      }

      // 1. Determine audience_list_id (either existing or upload new)
      let audience_list_id: string;
      if (formValues.recipientListId === "new") {
        if (file) {
          const uploadResult = await dispatch(
            uploadFileThunk(
              showCampaignMessage || !formValues.agent
                ? { file }
                : { file, agentId: formValues.agent }
            )
          ).unwrap();
          audience_list_id = uploadResult.data.audience_list_id;
          if (!audience_list_id) {
            toast.error(
              t("campaignModal.create.validation.failedToGetAudienceListId"),
              5
            );
            return;
          }
        } else {
          toast.error(
            t("campaignModal.create.validation.recipientRequired"),
            5
          );
          return;
        }
      } else {
        audience_list_id = formValues.recipientListId;
      }

      // 2. Prepare campaign payload (map form fields to API fields)
      const campaignPayload = {
        campaign_name: formValues.campaignName,
        phone_number: formValues.phoneNumber,
        ...(isCampaignGreetingChannel(formValues.channel || defaultChannel)
          ? {}
          : { agent_id: formValues.agent }),
        audience_list_id,
        campaign_description: formValues.campaignDescription,
        channel: formValues.channel || defaultChannel,
        schedule_timing: (timing === "immediate"
          ? "send immediately"
          : timing === "schedule"
          ? "schedule for later"
          : "save for testing") as
          | "send immediately"
          | "schedule for later"
          | "save for testing",
        start_at:
          timing === "schedule" && schedule.date && schedule.time
            ? (() => {
                const dateStr = schedule.date.format("YYYY-MM-DD");
                const timeStr = schedule.time.format("hh:mm");
                const ampm = schedule.ampm;

                // Convert 12-hour to 24-hour format
                let [hours, minutes] = timeStr.split(":");
                let hour = parseInt(hours);

                if (ampm === "PM" && hour !== 12) {
                  hour += 12;
                } else if (ampm === "AM" && hour === 12) {
                  hour = 0;
                }

                const formattedHour = hour.toString().padStart(2, "0");
                return `${dateStr}T${formattedHour}:${minutes}:00`;
              })()
            : undefined,
        time_zone: timing === "schedule" ? schedule.tz : undefined,
        number_of_recipients:
          formValues.recipientListId === "new"
            ? validCount || undefined
            : undefined,
        ...(isCampaignGreetingChannel(formValues.channel || defaultChannel)
          ? {
              greeting_message: String(
                formValues.greeting_message || ""
              ).trim(),
            }
          : {}),
      };

      // 3. Create campaign
      try {
        const createResult = await dispatch(createCampaignThunk(campaignPayload)).unwrap();
        await dispatch(
          getCampaignsThunk({
            limit: 10,
            page: 1,
            keyword: "",
            status: "",
            directionFilter: "",
            sort: { createdAt: -1 },
          })
        ).unwrap();
        toast.success(
          `${formValues.campaignName} ${t(
            "campaignModal.create.validation.campaignCreatedSuccessfully"
          )}`,
          5,
          <CheckCircle width={14} height={14} />
        );
        
        // Navigate to campaign detail page
        const campaignId = createResult?.data?.campaign_id;
        if (campaignId) {
          handleClose();
          navigate(`/outbound/${campaignId}`, {
            state: { channel: formValues.channel || defaultChannel },
          });
        } else {
          handleClose();
        }
      } catch (error) {
        console.log("error", error);
        setIsCreating(false);
        return;
      }
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        applyYupErrors(error);
      } else {
        toast.error(t("campaignModal.create.validation.errorOccurred"), 5);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreate = async () => {
    // Check file validation first - show confirm instead of blocking
    const isNewRecipient = recipientSelection === "new";
    if (
      isNewRecipient &&
      (fileValidationError || (invalidCount !== null && invalidCount > 0))
    ) {
      modal.confirm({
        title: t("campaignModal.create.proceedWithFileWarnings"),
        content: (
          <div>
            {fileValidationError ||
              `${invalidCount}/${totalCount} ${t(
                "campaignModal.create.invalidPhoneNumbers"
              )}`}
            <div style={{ marginTop: 8 }}>
              {t("campaignModal.create.fileWarningsDescription")}
            </div>
          </div>
        ),
        okText: t("campaignModal.create.continue"),
        cancelText: t("campaignModal.create.updateFile"),
        centered: true,
        zIndex: 1100,
        onOk: async () => {
          await performCreate();
        },
      });
      return;
    }

    await performCreate();
  };

  const applyYupErrors = (error: yup.ValidationError) => {
    const errors = error.inner.reduce((acc, err) => {
      if (err.path) {
        acc[err.path] = err.message;
      }
      return acc;
    }, {} as Record<string, string>);

    form.setFields(
      Object.keys(errors).map((key) => ({
        name: key,
        errors: [errors[key]],
      }))
    );
    toast.error(
      t("campaignModal.create.validation.pleaseFixValidationErrors"),
      5
    );
  };

  const validateDetails = async () => {
    const formValues = form.getFieldsValue();
    const validationData = {
      ...formValues,
      uploadedFile: file ? [file] : [],
    };
    const detailsSchema = campaignSchema.shape({
      greeting_message: yup.string().notRequired(),
    });
    await detailsSchema.validate(validationData, { abortEarly: false });
    if (timing === "schedule" && (!schedule.date || !schedule.time)) {
      toast.error(
        t("campaignModal.create.validation.scheduleDateAndTimeRequired"),
        5
      );
      return false;
    }
    const fields = [
      "campaignName",
      "phoneNumber",
      "channel",
      "recipientListId",
    ];
    if (!showCampaignMessage) fields.push("agent");
    if (formValues.recipientListId === "new") fields.push("uploadedFile");
    await form.validateFields(fields);
    return true;
  };

  const handleNext = async () => {
    try {
      const ok = await validateDetails();
      if (ok) setWizardStep(1);
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        applyYupErrors(error);
      }
    }
  };

  const handleClose = () => {
    form.resetFields();
    setFile(null);
    setValidCount(null);
    setInvalidCount(null);
    setFileValidationError(null);
    setIsDragging(false);
    setTiming("immediate");
    setSchedule({ date: null, time: null, ampm: "AM", tz: "EST" });
    setIsMissingPhoneColumn(false);
    setTotalCount(null);
    setRecipientSelection(null);
    setIsCreating(false);
    setWizardStep(0);
    lastPrefillRef.current = "";
    onClose();
  };

  const didInitPhone = useRef(false);

  useEffect(() => {
    fetchAgents();
    fetchLines();
    fetchAudiences();
  }, []);

  // Set default values for agent and phone number when data is available
  useEffect(() => {
    if (!open) {
      didInitPhone.current = false;
      lastPrefillRef.current = "";
      return;
    }

    const currentChannel = form.getFieldValue("channel");
    const currentAgent = form.getFieldValue("agent");
    const currentPhoneNumber = form.getFieldValue("phoneNumber");
    const phoneStillValid = campaignPhoneLines.some(
      (line: ILineResponse) => line.line_number === currentPhoneNumber
    );

    if (currentPhoneNumber && !phoneStillValid && isMessagingCampaign) {
      form.setFieldsValue({ phoneNumber: undefined });
    } else if (
      !didInitPhone.current &&
      !currentPhoneNumber &&
      campaignPhoneLines[0]?.line_number
    ) {
      form.setFieldsValue({ phoneNumber: campaignPhoneLines[0].line_number });
      didInitPhone.current = true;
    } else if (currentPhoneNumber) {
      didInitPhone.current = true;
    }

    if (
      availableChannels.length &&
      (!currentChannel ||
        !availableChannels.includes(currentChannel as ChannelKey))
    ) {
      form.setFieldsValue({ channel: defaultChannel });
    }

    const agentStillValid = campaignAgents.some(
      (agent) => agent._id === currentAgent
    );
    if (showCampaignMessage) {
      if (currentAgent) {
        form.setFieldsValue({ agent: undefined });
      }
    } else if (!agentStillValid) {
      const nextAgent = campaignAgents[0]?._id;
      if (nextAgent !== currentAgent) {
        form.setFieldsValue({ agent: nextAgent });
      }
    }
  }, [
    open,
    campaignAgents,
    campaignPhoneLines,
    isMessagingCampaign,
    showCampaignMessage,
    form,
    defaultChannel,
    availableChannels,
  ]);

  useEffect(() => {
    if (!open) {
      setWizardStep(0);
      return;
    }
    if (!showCampaignMessage && wizardStep === 1) {
      setWizardStep(0);
    }
  }, [open, showCampaignMessage, wizardStep]);

  useEffect(() => {
    if (!open || !showCampaignMessage || !selectedAgentId) return;
    const current = String(form.getFieldValue("greeting_message") || "");
    const currentTrim = current.trim();
    const lastTrim = String(lastPrefillRef.current || "").trim();
    const userEdited = currentTrim !== "" && currentTrim !== lastTrim;
    if (userEdited) return;
    form.setFieldsValue({ greeting_message: agentGreeting });
    lastPrefillRef.current = agentGreeting;
  }, [open, showCampaignMessage, selectedAgentId, agentGreeting, form]);

  const inboundReminder = selectedLineMissingInboundAgent ? (
    <InboundAgentAlert
      type="warning"
      showIcon
      message={t(
        selectedChannel === "email"
          ? "campaignModal.create.missingInboundAgentEmail"
          : "campaignModal.create.missingInboundAgent"
      )}
      description={t(
        selectedChannel === "email"
          ? "campaignModal.create.missingInboundAgentEmailDescription"
          : "campaignModal.create.missingInboundAgentDescription"
      )}
      action={
        <Button
          size="small"
          onClick={() => {
            handleClose();
            navigate(
              selectedChannel === "email" ? "/agents" : "/settings?tab=phones"
            );
          }}
        >
          {t(
            selectedChannel === "email"
              ? "campaignModal.create.goToAgents"
              : "campaignModal.create.goToSettings"
          )}
        </Button>
      }
    />
  ) : null;

  const primaryDisabled =
    isCreating ||
    (recipientSelection === "new" && isMissingPhoneColumn) ||
    !recipientSelection;

  return (
    <Modal
      title={
        <div style={{ position: "relative", margin: "0 -24px 0 -24px" }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 500,
              padding: "4px 0 16px 24px",
            }}
          >
            {t("campaignModal.create.title")}
          </div>
          {showPreviewStep && (
            <WizardSteps>
              <WizardStep
                type="button"
                $active={wizardStep === 0}
                $completed={wizardStep > 0}
                onClick={() => setWizardStep(0)}
              >
                <StepIndex $active={wizardStep === 0} $completed={wizardStep > 0}>
                  1
                </StepIndex>
                <StepLabel $active={wizardStep === 0}>
                  {t("campaignModal.create.stepDetails")}
                </StepLabel>
              </WizardStep>
              <WizardRail $completed={wizardStep > 0} />
              <WizardStep
                type="button"
                $active={wizardStep === 1}
                $completed={false}
                onClick={() => {
                  if (wizardStep === 1) return;
                  handleNext();
                }}
              >
                <StepIndex $active={wizardStep === 1} $completed={false}>
                  2
                </StepIndex>
                <StepLabel $active={wizardStep === 1}>
                  {t("campaignModal.create.stepPreview")}
                </StepLabel>
              </WizardStep>
            </WizardSteps>
          )}
          <div
            style={{
              borderBottom: `1px solid ${theme.colors.grey2}`,
              width: "100%",
              marginBottom: 24,
            }}
          />
        </div>
      }
      closeIcon={
        <CloseOutlined
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#000",
          }}
        />
      }
      modalRender={(modal) => <StyledModalWrapper>{modal}</StyledModalWrapper>}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={
        showPreviewStep && wizardStep === 1
          ? "min(960px, calc(100vw - 24px))"
          : "min(800px, calc(100vw - 24px))"
      }
    >
      <Form
        form={form}
        requiredMark={false}
        labelCol={formLabelCol}
        wrapperCol={formWrapperCol}
        labelAlign="left"
        validateTrigger="onChange"
      >
        <div style={{ display: onDetailsStep ? "block" : "none" }}>
        <Form.Item
          label={t("campaignModal.create.campaignName")}
          name="campaignName"
        >
          <Input
            size="large"
            placeholder={t("campaignModal.create.campaignNamePlaceholder")}
            allowClear
            onChange={() =>
              form.setFields([{ name: "campaignName", errors: [] }])
            }
          />
        </Form.Item>
        <Form.Item
          label={t("campaignModal.create.phoneNumber")}
          name="phoneNumber"
        >
          <Select
            size="large"
            placeholder={t("campaignModal.create.phoneNumberPlaceholder")}
            allowClear={isMessagingCampaign}
            onChange={() =>
              form.setFields([{ name: "phoneNumber", errors: [] }])
            }
          >
            {campaignPhoneLines.map((line: ILineResponse) => (
              <Option key={line._id} value={line.line_number}>
                {line.line_number}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {noOutboundMessagingNumbers && (
          <Form.Item wrapperCol={formOffsetCol}>
            <Alert
              type="warning"
              showIcon
              message={t("campaignModal.create.noOutboundNumbers")}
              description={t("campaignModal.create.noOutboundNumbersDescription")}
            />
          </Form.Item>
        )}
        <Form.Item
          label={t("campaignModal.create.channel")}
          name="channel"
          initialValue={defaultChannel}
          extra={t("campaignModal.create.channelNote")}
        >
          <Select
            size="large"
            placeholder={t("campaignModal.create.channelPlaceholder")}
            onChange={() => form.setFields([{ name: "channel", errors: [] }])}
          >
            {availableChannels.includes("call") && (
              <Option value="call">{t("dashboard.v2.phoneCall")}</Option>
            )}
            {availableChannels.includes("sms") && (
              <Option value="sms">{t("dashboard.v2.textMessage")}</Option>
            )}
            {availableChannels.includes("whatsapp") && (
              <Option value="whatsapp">{t("dashboard.v2.whatsapp")}</Option>
            )}
            {availableChannels.includes("email") && (
              <Option value="email">{t("dashboard.v2.email")}</Option>
            )}
          </Select>
        </Form.Item>
        {inboundReminder && (
          <div style={{ marginBottom: 16 }}>{inboundReminder}</div>
        )}
        {!showCampaignMessage && (
          <Form.Item label={t("campaignModal.create.agent")} name="agent">
            <Select
              size="large"
              placeholder={t("campaignModal.create.agentPlaceholder")}
              onChange={() => form.setFields([{ name: "agent", errors: [] }])}
            >
              {campaignAgents.map((agent) => (
                <Option key={agent._id} value={agent._id}>
                  {agent.agent_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
        <Form.Item
          label={t("campaignModal.create.recipients")}
          name="recipientListId"
        >
          <Select
            size="large"
            placeholder={t("campaignModal.create.recipientPlaceholder")}
            value={recipientSelection ?? undefined}
            onChange={(value) => {
              setRecipientSelection(value);
              form.setFields([{ name: "recipientListId", errors: [] }]);
              if (value !== "new") {
                setFile(null);
                setValidCount(null);
                setInvalidCount(null);
                setFileValidationError(null);
                setIsMissingPhoneColumn(false);
                setTotalCount(null);
              }
              form.setFieldsValue({ recipientListId: value });
            }}
          >
            <Option
              value="new"
              style={{
                fontWeight:
                  recipientSelection === "new" || recipientSelection === null
                    ? 600
                    : undefined,
                backgroundColor:
                  recipientSelection === "new" || recipientSelection === null
                    ? theme.colors.blue2
                    : undefined,
              }}
            >
              {t("campaignModal.create.addNewRecipientList")}
            </Option>
            {audiences.map((aud: any) => (
              <Option key={aud._id} value={aud._id}>
                {aud.audience_list_name || "-"}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {recipientSelection === "new" && (
          <Form.Item name="uploadedFile" wrapperCol={formOffsetCol}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 16,
              }}
            >
              {file && (
                <UploadedFileInfoRow>
                  <CheckCircle />
                  <UploadedFileText>
                    {t("campaignModal.create.fileUploaded")}{" "}
                    <Tooltip title={file.name} placement="top">
                      <UploadedFileName>{file.name}</UploadedFileName>
                    </Tooltip>
                  </UploadedFileText>
                  {!fileValidationError && (
                    <UploadedFileMeta>
                      ({validCount}{" "}
                      {t("campaignModal.create.uniqueRecipientsFound")})
                    </UploadedFileMeta>
                  )}
                </UploadedFileInfoRow>
              )}
              {fileValidationError && (
                <Alert
                  type="warning"
                  message={
                    isMissingPhoneColumn ? (
                      <span>
                        {t("campaignModal.create.phoneNumberColumnMissing")}
                      </span>
                    ) : (
                      <span>
                        <b>
                          {invalidCount}/{totalCount}
                        </b>{" "}
                        {t("campaignModal.create.invalidPhoneNumbers")}
                      </span>
                    )
                  }
                  description={
                    isMissingPhoneColumn
                      ? t(
                          "campaignModal.create.phoneNumberColumnMissingDescription"
                        )
                      : t("campaignModal.create.invalidPhoneNumbersDescription")
                  }
                  showIcon
                  closable={!isMissingPhoneColumn}
                  onClose={() => setFileValidationError(null)}
                  style={{ fontSize: 14, marginTop: -8 }}
                />
              )}
              {recipientSelection === "new" && (
                <UploadButtonRow>
                  <Upload
                    name="file"
                    fileList={
                      file
                        ? [{ uid: "1", name: file.name, status: "done" }]
                        : []
                    }
                    beforeUpload={() => false}
                    onChange={(info) => {
                      handleFileChange(info);
                      form.setFields([{ name: "uploadedFile", errors: [] }]);
                    }}
                    accept=".csv"
                    maxCount={1}
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />} size="large">
                      {t("campaignModal.create.uploadFile")}
                    </Button>
                  </Upload>
                  <span style={{ color: theme.colors.grey1, fontSize: 14 }}>
                    {t("campaignModal.create.maxFileSize")}
                  </span>
                </UploadButtonRow>
              )}
              {recipientSelection === "new" && !file && (
                <DragDropArea
                  onDragOver={() => setIsDragging(true)}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={() => setIsDragging(false)}
                >
                  <Upload.Dragger
                    name="file"
                    fileList={[]}
                    beforeUpload={() => false}
                    onChange={(info) => {
                      handleFileChange(info);
                      form.setFields([{ name: "uploadedFile", errors: [] }]);
                    }}
                    accept=".csv"
                    maxCount={1}
                    style={{
                      borderRadius: 8,
                      background: isDragging
                        ? theme.colors.green2
                        : theme.colors.grey4,
                      borderColor: isDragging
                        ? theme.colors.green1
                        : theme.colors.grey1,
                      borderWidth: 2,
                      transition: "all 0.2s",
                    }}
                    height={120}
                    showUploadList={false}
                  >
                    <div
                      style={{
                        color: isDragging
                          ? theme.colors.green1
                          : theme.colors.grey1,
                        fontSize: 15,
                        textAlign: "center",
                      }}
                    >
                      {t("campaignModal.create.dragDropText")}
                      <br />
                      {t("campaignModal.create.dragDropSubtext")}
                    </div>
                  </Upload.Dragger>
                </DragDropArea>
              )}
            </div>
          </Form.Item>
        )}
        {recipientSelection === "new" && (
          <Form.Item wrapperCol={formOffsetCol}>
            <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 2 }}>
              {t("campaignModal.create.formatting")}
            </div>
            <div
              style={{
                color: theme.colors.grey1,
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              {t("campaignModal.create.formattingDescription")}
              <br />
              {t(
                showCampaignMessage
                  ? "campaignModal.create.formattingDescription2Messaging"
                  : "campaignModal.create.formattingDescription2"
              )}{" "}
              <a
                href={templateCsvHref}
                download={templateCsvHref ? downloadTemplateFilename : undefined}
                onClick={(e) => {
                  if (!showCampaignMessage && !selectedAgent) {
                    e.preventDefault();
                    toast.error(
                      t("campaignModal.create.validation.agentRequired"),
                      5
                    );
                  } else if (!templateCsvHref) {
                    e.preventDefault();
                  }
                }}
                style={{
                  color: theme.colors.blue1,
                  textDecoration: "underline",
                }}
              >
                {t("campaignModal.create.downloadTemplate")}
              </a>
            </div>
          </Form.Item>
        )}
        <Form.Item
          label={t("campaignModal.create.timing")}
          required
          style={{ marginBottom: 0 }}
        >
          <Radio.Group
            value={timing}
            onChange={(e) => setTiming(e.target.value)}
            style={{ display: "flex", gap: 32 }}
          >
            <Radio value="immediate">
              {t("campaignModal.create.sendImmediately")}
            </Radio>
            <Radio value="schedule">
              {t("campaignModal.create.scheduleForLater")}
            </Radio>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Radio value="test">
                {t("campaignModal.create.saveForTesting")}
              </Radio>
              {timing === "test" && (
                <Tooltip
                  title={t("campaignModal.create.saveForTestingTooltip")}
                  placement="right"
                >
                  <InfoCircleOutlined
                    style={{
                      color: theme.colors.grey1,
                      cursor: "pointer",
                      fontSize: 15,
                      marginLeft: 2,
                    }}
                  />
                </Tooltip>
              )}
            </span>
          </Radio.Group>
        </Form.Item>
        {timing === "schedule" && (
          <Form.Item
            label={t("campaignModal.common.schedule")}
            required
            style={{ marginTop: 16, marginBottom: 0 }}
          >
            <ScheduleFields>
              <Select
                size="large"
                value={schedule.tz}
                onChange={(tz) => {
                  setSchedule((s) => ({ ...s, tz }));
                  setSchedule((s) => ({ ...s, date: null }));
                }}
                style={{ width: 80 }}
              >
                {getTimezoneOptions().map((tz) => (
                  <Option key={tz.value} value={tz.value}>
                    {tz.label}
                  </Option>
                ))}
              </Select>
              <DatePicker
                size="large"
                placeholder={t("campaignModal.common.scheduleDatePlaceholder")}
                value={schedule.date}
                onChange={(date) => setSchedule((s) => ({ ...s, date }))}
                disabledDate={(current) => {
                  if (!current) return false;

                  // Normalize current to start of day (remove time component)
                  const currentDate = current.startOf("day");

                  // Get today's date in the selected timezone
                  const timezoneName =
                    TIMEZONE_CONFIG[
                      schedule.tz as keyof typeof TIMEZONE_CONFIG
                    ];
                  const todayInTimezone = timezoneName
                    ? dayjs().tz(timezoneName).startOf("day")
                    : dayjs().startOf("day");

                  // Compare normalized calendar date with today in selected timezone
                  const calendarDateStr = currentDate.format("YYYY-MM-DD");
                  const todayStr = todayInTimezone.format("YYYY-MM-DD");

                  return calendarDateStr < todayStr;
                }}
              />
              <TimePicker
                popupClassName="hide-ampm"
                size="large"
                placeholder={t("campaignModal.common.scheduleTimePlaceholder")}
                format="hh:mm"
                use12Hours
                value={schedule.time}
                onChange={(time) => setSchedule((s) => ({ ...s, time }))}
              />
              <Select
                size="large"
                value={schedule.ampm}
                onChange={(ampm) => setSchedule((s) => ({ ...s, ampm }))}
                style={{ width: 80 }}
              >
                <Option value="AM">AM</Option>
                <Option value="PM">PM</Option>
              </Select>
            </ScheduleFields>
          </Form.Item>
        )}
        </div>
        {showPreviewStep && (
          <div style={{ display: wizardStep === 1 ? "block" : "none" }}>
            {inboundReminder && (
              <div style={{ marginBottom: 16 }}>{inboundReminder}</div>
            )}
            <Form.Item
              name="greeting_message"
              labelCol={{ span: 0 }}
              wrapperCol={{ span: 24 }}
              style={{ marginBottom: 0 }}
              colon={false}
            >
              <CampaignMessagePreview
                channel={selectedChannel}
                resetToken={`${open ? "open" : "closed"}-${selectedChannel}`}
              />
            </Form.Item>
          </div>
        )}
        <Form.Item wrapperCol={{ span: 24 }} style={{ marginTop: 32, marginBottom: 0 }}>
          <FooterActions $hasBack={showPreviewStep && wizardStep === 1}>
            {showPreviewStep && wizardStep === 1 && (
              <FooterButton type="button" onClick={() => setWizardStep(0)}>
                {t("campaignModal.common.back")}
              </FooterButton>
            )}
            <FooterRight>
              <FooterButton type="button" onClick={handleClose}>
                {t("campaignModal.common.cancel")}
              </FooterButton>
              {showPreviewStep && wizardStep === 0 ? (
                <FooterButton
                  type="button"
                  primary
                  onClick={handleNext}
                  disabled={primaryDisabled}
                  style={{
                    opacity: primaryDisabled ? 0.5 : 1,
                    cursor: primaryDisabled ? "not-allowed" : "pointer",
                  }}
                >
                  {t("campaignModal.create.next")}
                </FooterButton>
              ) : (
                <FooterButton
                  type="button"
                  primary
                  onClick={handleCreate}
                  disabled={primaryDisabled}
                  style={{
                    opacity: primaryDisabled ? 0.5 : 1,
                    cursor: primaryDisabled ? "not-allowed" : "pointer",
                  }}
                >
                  {t("campaignModal.common.create")}
                </FooterButton>
              )}
            </FooterRight>
          </FooterActions>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const StyledModalWrapper = styled.div`
  .ant-modal-close {
    right: 24px;
    top: 20px;
  }

  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-form-item-explain-error {
    font-size: 12px;
  }

  .ant-form-item-label > label {
    font-weight: 600;
    font-size: 16px;
    white-space: normal;
  }

  .ant-form-item:has(textarea) .ant-form-item-label {
    padding-top: 8px;
  }

  .ant-upload button {
    background: ${theme.colors.grey3};
    border-color: ${theme.colors.grey2};
  }
`;

const WizardSteps = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  padding: 0 24px 16px;
`;

const WizardStep = styled.button<{ $active?: boolean; $completed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  min-width: 0;
`;

const StepIndex = styled.span<{ $active?: boolean; $completed?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  border: 2px solid
    ${({ $active, $completed }) =>
      $active || $completed ? theme.colors.green1 : theme.colors.grey1};
  background: ${({ $active, $completed }) =>
    $active || $completed ? theme.colors.green1 : "#fff"};
  color: ${({ $active, $completed }) =>
    $active || $completed ? "#fff" : theme.colors.grey1};
`;

const StepLabel = styled.span<{ $active?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? theme.colors.green1 : theme.colors.grey1)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WizardRail = styled.div<{ $completed?: boolean }>`
  height: 1px;
  width: 72px;
  background: ${({ $completed }) =>
    $completed ? theme.colors.green1 : theme.colors.grey2};

  @media (max-width: 640px) {
    width: 24px;
  }
`;

const InboundAgentAlert = styled(Alert)`
  background: ${theme.colors.yellow3};
  border: 1px solid rgba(253, 196, 72, 0.7);
  align-items: flex-start;

  .ant-alert-icon {
    color: ${theme.colors.yellow4};
    margin-top: 2px;
  }

  .ant-alert-message {
    font-weight: 600;
    color: #1f2937;
  }

  .ant-alert-description {
    color: #374151;
  }

  .ant-alert-action {
    margin-left: 12px;
    align-self: center;
  }

  .ant-alert-action .ant-btn {
    background: #fff;
    border-color: ${theme.colors.grey2};
  }
`;

const UploadedFileInfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  height: 30px;
`;

const UploadedFileText = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: ${theme.colors.grey1};
`;

const UploadedFileName = styled.span`
  color: #000;
`;

const UploadedFileMeta = styled.span`
  color: ${theme.colors.grey1};
  font-size: 16px;
  font-weight: 400;
`;

const UploadButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
`;

const DragDropArea = styled.div``;

const ScheduleFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;

  @media (max-width: 640px) {
    .ant-select,
    .ant-picker {
      flex: 1 1 140px;
      width: 100% !important;
    }
  }
`;

const FooterActions = styled.div<{ $hasBack?: boolean }>`
  display: flex;
  justify-content: ${({ $hasBack }) => ($hasBack ? "space-between" : "flex-end")};
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  border-top: 1px solid ${theme.colors.grey2};
  margin: 0 -24px -8px;
  padding: 16px 24px 0;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    align-items: stretch;

    button {
      width: 100%;
    }
  }
`;

const FooterRight = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-left: auto;

  @media (max-width: 480px) {
    width: 100%;
    flex-direction: column-reverse;
  }
`;

const FooterButton = styled.button<{ primary?: boolean; disabled?: boolean }>`
  background: ${({ primary, disabled }) =>
    disabled
      ? theme.colors.grey2
      : primary
      ? theme.colors.green1
      : theme.colors.grey3};
  color: ${({ primary, disabled }) =>
    disabled ? theme.colors.grey1 : primary ? "#fff" : "#000"};
  border: 1px solid
    ${({ primary, disabled }) =>
      disabled
        ? theme.colors.grey2
        : primary
        ? theme.colors.green1
        : theme.colors.grey2};
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;

  &:hover {
    background: ${({ primary, disabled }) =>
      disabled
        ? theme.colors.grey2
        : primary
        ? theme.colors.green1
        : theme.colors.grey3};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

export default CreateCampaignModal;
